import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { HashRouter, route } from './HashRouter';
import { SettingsUI } from './settings/SettingsUI';
import { GraphUI } from './graph/GraphUI';
import { GraphVizPage } from './graph/GraphVizPage';
import { VertexList } from './VertexList';
import { ArchiveViewer } from './ArchiveViewer';
import { Layout } from './Layout';
import { QuickTipsOverlay } from './QuickTipsOverlay';
import { VertexSetsPage } from './sets/VertexSetsPage';
import { VertexSetDetail } from './sets/VertexSetDetail';
import { BillingProvider } from '../contexts/BillingContext';
import { AuthProvider, useAuth } from '../contexts/AuthContext';

interface GraphRouteProps {
  vertex?: string;
  trail?: string;  // Breadcrumb trail: comma-separated encoded vertex URLs
  from?: string;
}

interface ArchiveRouteProps {
  vertex?: string;
  timestamp?: string;
  times?: string;
  from?: string;
  context?: string;
  limit?: string;
}

function GraphRoute({ vertex, trail, from }: GraphRouteProps) {
  if (!vertex) {
    route('/', true);
    return null;
  }

  // URLSearchParams already decoded `from` once; do not decode again or the
  // nested chain gets over-decoded (raw `?` / `/` chars leak out and break
  // subsequent router parsing).
  const backPath = from ?? '/';

  return (
    <Layout showSearch={false}>
      <GraphUI
        vertexUrl={decodeURIComponent(vertex)}
        trail={trail}
        onClose={() => route(backPath)}
      />
    </Layout>
  );
}

function ArchiveRoute({ vertex, timestamp, times, from, context, limit }: ArchiveRouteProps) {
  if (!vertex) {
    route('/', true);
    return null;
  }

  const decodedVertex = decodeURIComponent(vertex);
  const decodedTimestamp = timestamp ? decodeURIComponent(timestamp) : undefined;

  // Parse available times from route query param
  const availableTimes = times
    ? JSON.parse(decodeURIComponent(times))
    : undefined;

  // Determine back navigation. HashRouter's URLSearchParams already
  // decodes query values once; decoding again over-decodes nested `from`
  // chains (see GraphRoute for the same fix).
  const backPath = from ?? '/';

  return (
    <Layout showSearch={false}>
      <ArchiveViewer
        vertexUrl={decodedVertex}
        timestamp={decodedTimestamp}
        availableTimes={availableTimes}
        context={context as 'coldstorage' | 'expiring' | undefined}
        backendConfiguredLimit={limit ? parseInt(limit, 10) : undefined}
        onBack={() => route(backPath)}
        onNavigate={(newTimestamp) => {
          const encodedVertex = encodeURIComponent(decodedVertex);
          const encodedTimestamp = encodeURIComponent(newTimestamp);
          const fromParam = from ? `&from=${from}` : '';
          route(`/archive/${encodedVertex}/${encodedTimestamp}?${fromParam}`);
        }}
      />
    </Layout>
  );
}

interface HomeRouteProps {
  view?: string;
  authority?: string;
  showTips?: string;
}

function HomeRoute({ view, authority, showTips }: HomeRouteProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [tipsVisible, setTipsVisible] = useState(showTips === 'true');

  // 'auto' means no explicit view mode in URL - let VertexList decide based on count
  const viewMode = view === 'authority' ? 'authority' : view === 'flat' ? 'flat' : 'auto';
  const selectedAuthority = authority ? decodeURIComponent(authority) : null;

  // The list URL that detail views should return to when closed. Preserving
  // view mode + selected authority keeps back navigation on the same slice
  // instead of the top-level authority selector.
  const currentListPath = (() => {
    const params = new URLSearchParams();
    if (view) params.set('view', view);
    if (selectedAuthority) params.set('authority', selectedAuthority);
    return params.toString() ? `/?${params.toString()}` : '/';
  })();
  const encodedFrom = encodeURIComponent(currentListPath);

  const dismissTips = () => {
    setTipsVisible(false);
    route('/');
  };

  return (
    <Layout showSearch={true} onSearchChange={setSearchQuery}>
      <VertexList
        searchQuery={searchQuery}
        viewMode={viewMode}
        selectedAuthority={selectedAuthority}
        onViewModeChange={(newMode, newAuthority) => {
          if (newMode === 'flat') {
            route('/?view=flat');
          } else if (newAuthority) {
            route(`/?view=authority&authority=${encodeURIComponent(newAuthority)}`);
          } else {
            route('/?view=authority');
          }
        }}
        onViewArchive={(url, timestamp, availableTimes) => {
          const encodedVertex = encodeURIComponent(url);
          const encodedTimestamp = encodeURIComponent(timestamp);
          const encodedTimes = encodeURIComponent(JSON.stringify(availableTimes));
          route(`/archive/${encodedVertex}/${encodedTimestamp}?times=${encodedTimes}&from=${encodedFrom}`);
        }}
        onOpenGraph={(url) => route(`/graph/${encodeURIComponent(url)}?from=${encodedFrom}`)}
        onOpenSet={(setId) => route(`/vertex-sets/${setId}?from=${encodedFrom}`)}
      />
      {tipsVisible && <QuickTipsOverlay onDismiss={dismissTips} />}
    </Layout>
  );
}

function SettingsRoute() {
  return (
    <Layout showSearch={false}>
      <SettingsUI />
    </Layout>
  );
}

function GraphVizRoute() {
  return (
    <Layout showSearch={false}>
      <GraphVizPage onClose={() => route('/')} />
    </Layout>
  );
}

function VertexSetsRoute() {
  return (
    <Layout showSearch={false}>
      <VertexSetsPage />
    </Layout>
  );
}

interface VertexSetDetailRouteProps {
  setId?: string;
  from?: string;
}

function VertexSetDetailRoute({ setId, from }: VertexSetDetailRouteProps) {
  if (!setId) {
    route('/vertex-sets', true);
    return null;
  }
  const backPath = from ?? '/vertex-sets';
  return (
    <Layout showSearch={false}>
      <VertexSetDetail setId={setId} onClose={() => route(backPath)} />
    </Layout>
  );
}

function AppRouter() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (!user && !window.location.hash.startsWith('#/settings')) {
      window.location.href = '/onboarding';
    }
  }, [loading, user]);

  if (loading) {
    return <div class="app-loading">Loading...</div>;
  }

  return (
    <HashRouter>
      <HomeRoute path="/" default />
      <ArchiveRoute path="/archive/:vertex/:timestamp" />
      <ArchiveRoute path="/archive/:vertex" />
      <SettingsRoute path="/settings" />
      <GraphRoute path="/graph/:vertex" />
      <GraphVizRoute path="/graph-viz" />
      <VertexSetsRoute path="/vertex-sets" />
      <VertexSetDetailRoute path="/vertex-sets/:setId" />
    </HashRouter>
  );
}

export function App() {
  return (
    <AuthProvider>
      <BillingProvider>
        <AppRouter />
      </BillingProvider>
    </AuthProvider>
  );
}
